import * as React from 'react';
import {
  getPlaceHolderTaxonomyIndustry,
  mapIOptionsCompanies,
  mapIOptionsCountries,
  mapIOptionsTotalCountries,
  mapIOptionsIndustries,
  mapIOptionsPlantUnits,
  mapFilteredCountries,
  mapAllCountries,
} from './UserManagementAssignRolesSelector.mapper';
import { IRolesState } from '../../../../../store/roles/store';
import { useSelector } from 'react-redux';
import { StoreState } from '../../../../../store/store';
import { IOption } from '@skf/bt-wind-rail-commonlibrary/dist/components/form/types';
import { useEffect, useState } from 'react';
import { SelectedRoleInfo } from '../../../../../store/roles/types';
import { useTranslation } from 'react-i18next';
import {
  ButtonPrimary,
  SelectInputSearch,
} from '@skf/bt-wind-rail-commonlibrary';
import { IUsersState } from '../../../../../store/users/store';
import { orderByAlphabetical } from '../../../../../services/stringServices';

interface IUserManagementAssignRolesSelectorProps {
  onAdd: (selectorRoleInfo: SelectedRoleInfo) => any;
}

export const UserManagementAssignRolesSelector: React.FunctionComponent<IUserManagementAssignRolesSelectorProps> = ({
  onAdd,
}) => {
  const { t } = useTranslation();

  const { rolesInfo }: IRolesState = useSelector<StoreState, IRolesState>(
    (state) => state.roles
  );

  const { user }: IUsersState = useSelector<StoreState, IRolesState>(
    (state) => state.users
  );

  const [selectorRoleInfo, setSelectorRoleInfo] = useState<SelectedRoleInfo>(
    {}
  );
  const [industryOptions, setIndustryOptions] = useState<IOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<IOption[]>([]);
  const [totalCountryOptions, setTotalCountryOptions] = useState<IOption[]>([]);
  const [sumatoryCountryOptions, setSumatoryCountryOptions] = useState<
    IOption[]
  >([]);
  const [companyOptions, setCompanyOptions] = useState<IOption[]>([]);
  const [plantUnitOptions, setPlantUnitOptions] = useState<IOption[]>([]);

  useEffect(() => {
    setIndustryOptions(mapIOptionsIndustries(rolesInfo));
    setCountryOptions(mapIOptionsCountries(rolesInfo, selectorRoleInfo));
    setTotalCountryOptions(
      mapIOptionsTotalCountries(rolesInfo, selectorRoleInfo)
    );
    setCompanyOptions(mapIOptionsCompanies(rolesInfo, selectorRoleInfo));
    setPlantUnitOptions(mapIOptionsPlantUnits(t, rolesInfo, selectorRoleInfo));
  }, [rolesInfo, selectorRoleInfo]);

  useEffect(() => {
    let auxcountryOptions = countryOptions.sort((a, b) =>
      orderByAlphabetical(a.label, b.label, true)
    );
    let auxtotalCountryOptions = totalCountryOptions.sort((a, b) =>
      orderByAlphabetical(a.label, b.label, true)
    );
    auxcountryOptions = auxcountryOptions.filter(
      (thing, index, self) =>
        index ===
        self.findIndex(
          (opction: IOption) =>
            opction.label === thing.label && opction.value === thing.value
        )
    );
    auxtotalCountryOptions = auxtotalCountryOptions.filter(
      (thing, index, self) =>
        index ===
        self.findIndex(
          (opction: IOption) =>
            opction.label === thing.label && opction.value === thing.value
        )
    );

    const options = [
      {
        label: 'Countries',
        options: [...auxcountryOptions],
      },
      {
        label: 'Other countries',
        options: [...auxtotalCountryOptions],
      },
    ];
    // @ts-ignore
    setSumatoryCountryOptions(options);
  }, [totalCountryOptions, countryOptions]);

  const onSelectIndustry = (option?: IOption) => {
    setSelectorRoleInfo({
      industryCode: option ? (option.value as number) : -1,
      industryName: option ? option.label : undefined,
      industryText: option ? option.label : undefined,
      siteCountryCode: undefined,
      siteCountryDescription: undefined,
      companyId: undefined,
      companyName: undefined,
      plantUnitId: undefined,
      plantUnitDescription: undefined,
    });
  };

  const onSelectCountry = (option?: IOption) => {
    const newSelectorRoleInfo: SelectedRoleInfo = {
      ...selectorRoleInfo,
      siteCountryCode: option ? (option.value as string) : undefined,
      siteCountryDescription: option ? (option.label as string) : undefined,
      companyId: undefined,
      companyName: selectorRoleInfo.companyName || undefined,
      plantUnitId: undefined,
      plantUnitDescription: undefined,
    };

    const allCountries = mapAllCountries(rolesInfo, newSelectorRoleInfo);

    const countryFiltered = allCountries.find(
      (x) => x.siteCountryCode === option?.value
    );

    const existCompanyInCountry =
      countryFiltered?.companies.find(
        (y) => y.companyId === selectorRoleInfo.companyId
      ) !== undefined;

    const countryUndefinedId = option ? undefined : selectorRoleInfo.companyId;
    const countryUndefinedName = option
      ? undefined
      : selectorRoleInfo.companyName;

    newSelectorRoleInfo.companyId = existCompanyInCountry
      ? selectorRoleInfo.companyId
      : countryUndefinedId;

    newSelectorRoleInfo.companyName = existCompanyInCountry
      ? selectorRoleInfo.companyName
      : countryUndefinedName;

    setSelectorRoleInfo(newSelectorRoleInfo);
  };

  const onSelectCompany = (option?: IOption) => {
    const newSelectorRoleInfo = {
      ...selectorRoleInfo,
      companyId: option ? (option.value as string) : undefined,
      companyName: option ? (option.label as string) : undefined,
      plantUnitId: undefined,
      plantUnitDescription: undefined,
    };

    const newCountries = mapFilteredCountries(rolesInfo, newSelectorRoleInfo);
    const existingCountry = newCountries.find(
      (x) => x.siteCountryCode === newSelectorRoleInfo.siteCountryCode
    );
    newSelectorRoleInfo.siteCountryCode = existingCountry?.siteCountryCode;
    newSelectorRoleInfo.siteCountryDescription =
      existingCountry?.siteCountryDescription;

    setCountryOptions(mapIOptionsCountries(rolesInfo, newSelectorRoleInfo));
    setTotalCountryOptions(
      mapIOptionsTotalCountries(rolesInfo, newSelectorRoleInfo)
    );

    setSelectorRoleInfo(newSelectorRoleInfo);
  };

  const onSelectPlantUnit = (option?: IOption) => {
    setSelectorRoleInfo({
      ...selectorRoleInfo,
      plantUnitId: option ? (option.value as string) : undefined,
      plantUnitDescription: option ? (option.label as string) : undefined,
    });
  };

  const onAddSelectedAssignedRole = () => {
    const newSelectedRoleInfo = { ...selectorRoleInfo };

    newSelectedRoleInfo.companyId = checkPropertyUndefined(
      newSelectedRoleInfo.companyId
    );
    newSelectedRoleInfo.companyName = checkPropertyUndefined(
      newSelectedRoleInfo.companyName
    );
    newSelectedRoleInfo.siteCountryCode = checkPropertyUndefined(
      newSelectedRoleInfo.siteCountryCode
    );
    newSelectedRoleInfo.siteCountryDescription = checkPropertyUndefined(
      newSelectedRoleInfo.siteCountryDescription
    );
    newSelectedRoleInfo.plantUnitDescription = checkPropertyUndefined(
      newSelectedRoleInfo.plantUnitDescription
    );
    newSelectedRoleInfo.plantUnitId = checkPropertyUndefined(
      newSelectedRoleInfo.plantUnitId
    );
    onAdd(newSelectedRoleInfo);
  };

  const checkPropertyUndefined = (property?: string) => {
    return property ? property : 'All';
  };

  return (
    <>
      <SelectInputSearch
        placeHolder={t('Industry')}
        translate={t}
        options={industryOptions.sort((a, b) =>
          orderByAlphabetical(a.label, b.label, true)
        )}
        onSelectedItemChanged={onSelectIndustry}
      />
      <SelectInputSearch
        placeHolder={t('Country')}
        options={sumatoryCountryOptions}
        isDisabled={countryOptions.length === 0}
        onSelectedItemChanged={onSelectCountry}
        value={
          selectorRoleInfo.siteCountryCode !== undefined
            ? selectorRoleInfo.siteCountryCode
            : -1
        }
        customValueValidation={true}
      />
      <SelectInputSearch
        placeHolder={t('Company')}
        options={companyOptions.sort((a, b) =>
          orderByAlphabetical(a.label, b.label, true)
        )}
        isDisabled={companyOptions.length === 0}
        value={
          selectorRoleInfo.companyId !== undefined
            ? selectorRoleInfo.companyId
            : -1
        }
        customValueValidation={true}
        onSelectedItemChanged={onSelectCompany}
      />
      <SelectInputSearch
        placeHolder={getPlaceHolderTaxonomyIndustry(
          t,
          selectorRoleInfo?.industryCode
        )}
        options={plantUnitOptions.sort((a, b) =>
          orderByAlphabetical(a.label, b.label, true)
        )}
        isDisabled={plantUnitOptions.length === 0}
        value={
          selectorRoleInfo.plantUnitId !== undefined
            ? selectorRoleInfo.plantUnitId
            : -1
        }
        customValueValidation={true}
        onSelectedItemChanged={onSelectPlantUnit}
      />
      <ButtonPrimary
        onClick={onAddSelectedAssignedRole}
        disabled={!selectorRoleInfo.industryName}
      >
        {t('Add')}
      </ButtonPrimary>
    </>
  );
};
